// exportRecallListsToExcel.ts
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { toBuddhistYear } from 'utils/app.utils'

export interface RecallListRow {
    branchCode: string
    branchCnNumber: string
    dateOfLastService: string  // ISO
    patientCode: string
    patientName: string
    phone: string
    operativeTypeName: string
    operativeName: string
    doctorName: string  // firstname + ' ' + lastname
    duration: number
    recallUnit: string  // e.g. "DAY"
    nextRecall: string  // ISO
    lastRecall: string  // ISO
}

export interface RecallListReport {
    title: string    // e.g. "Recall Lists"
    filters: string // e.g. ["ค้นหา: …", "สาขา: …", …]
    rows: RecallListRow[],
    t: any
}

export async function exportRecallListsToExcel(rpt: RecallListReport) {
    const wb = new ExcelJS.Workbook()
    const ws = wb.addWorksheet(rpt.title, {
        pageSetup: { orientation: 'landscape' }
    })
    ws.properties.defaultRowHeight = 18

    // 1) คอลัมน์ A–L
    ws.columns = [
        { header: rpt.t('TABLE.NO'), width: 6 },
        { header: rpt.t('RECALL_LISTS.TABLE.CELLS.BRANCH_CODE'), width: 15 },
        { header: rpt.t('RECALL_LISTS.TABLE.CELLS.DATE_OF_LAST_SERVICE'), width: 20 },
        { header: rpt.t('RECALL_LISTS.TABLE.CELLS.PATIENT_CODE'), width: 15 },
        { header: rpt.t('RECALL_LISTS.TABLE.CELLS.PATIENT_NAME'), width: 20 },
        { header: rpt.t('RECALL_LISTS.TABLE.CELLS.PHONE'), width: 15 },
        { header: rpt.t('RECALL_LISTS.TABLE.CELLS.OPERATIVE_TYPE_NAME'), width: 20 },
        { header: rpt.t('RECALL_LISTS.TABLE.CELLS.OPERATIVE_NAME'), width: 25 },
        { header: rpt.t('RECALL_LISTS.TABLE.CELLS.DOCTOR'), width: 25 },
        { header: rpt.t('RECALL_LISTS.TABLE.CELLS.DURATION'), width: 25 },
        { header: rpt.t('RECALL_LISTS.TABLE.CELLS.DATE'), width: 20 },
        { header: rpt.t('RECALL_LISTS.TABLE.CELLS.NEXT_RECALL'), width: 20 },
    ] as ExcelJS.Column[]

    let r = 1

    // 2) Title (A1–L1)
    ws.mergeCells(r, 1, r, ws.columns.length)
    ws.getCell(r, 1).value = rpt.title
    ws.getCell(r, 1).font = { size: 16, bold: true }
    ws.getCell(r, 1).alignment = { horizontal: 'center', vertical: 'middle' }
    r++

    // 3) Filters แถว 2
    ws.mergeCells(r, 1, r, ws.columns.length)
    ws.getCell(r, 1).value = rpt.filters
    ws.getCell(r, 1).font = { size: 12 }
    ws.getCell(r, 1).alignment = { horizontal: 'left', vertical: 'middle' }
    r += 2

    // 4) Header row
    const header = ws.addRow(ws.columns.map(c => (c.header as string)))
    header.font = { bold: true }
    header.alignment = { horizontal: 'center', vertical: 'middle' }
    header.height = 20
    header.eachCell(cell => {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
    })
    r++

    if (!rpt.rows || rpt.rows.length === 0) {
        const noDataRow = ws.addRow([rpt.t('REPORT.TITLE.NO_DATA')])
        ws.mergeCells(noDataRow.number, 1, noDataRow.number, ws.columns.length)
        noDataRow.font = { italic: true, color: { argb: 'FF000000' } }
        noDataRow.alignment = { horizontal: 'center' }
        const buf = await wb.xlsx.writeBuffer()
        saveAs(
            new Blob([buf]),
            `${rpt.title}_${moment().format('YYYYMMDD')}.xlsx`
        )
        return
    }

    // 5) Data rows
    rpt.rows.forEach((d: any, i) => {
        const lastSvc = d.dateOfLastService
            ? `${toBuddhistYear(moment(d.dateOfLastService))} ${moment(d.dateOfLastService).format('HH:mm')} น.`
            : '-'
        const nextRc = d.status === 'NORMAL' && (d.nextRecall
            ? `${toBuddhistYear(moment(d.nextRecall))} 07:00`
            : '-') || 'ยกเลิก'
        const lastRc = d.status === 'NORMAL' && (d.lastRecall
            ? `${toBuddhistYear(moment(d.lastRecall))} ${moment(d.lastRecall).format('HH:mm')}`
            : '-') || 'ยกเลิก'

        const row = ws.addRow([
            i + 1,
            `${d.branchCode}${d.branchCnNumber}`,
            lastSvc,
            d.patientCode,
            d.patientName,
            d.phone,
            d.operativeTypeName,
            d.operativeName,
            d.doctorName,
            `${d.duration} ${d.recallUnit}`,
            d.nextRecall,
            d.lastRecall
        ])
        row.alignment = { vertical: 'middle' }
        row.eachCell((cell, col) => {
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
            if (col === 1) {
                cell.alignment = { horizontal: 'center', vertical: 'middle' }
            } else {
                cell.alignment = { horizontal: 'left', vertical: 'middle' }
            }
        })
    })

    // 6) Save
    const buf = await wb.xlsx.writeBuffer()
    saveAs(new Blob([buf]), `${rpt.title}_${moment().format('YYYYMMDD')}.xlsx`)
}
